/**
 * Settlement workbook — the closer's pre-close HUD / ALTA balancing workbook
 * (eastview-os frontend_documentation/settlement-workbook.md, backend
 * 2026-09-24, routes in app/routers/closing.py).
 *
 * Pre-close only: every route answers 409 on an acquisition. Every workbook
 * response is `{ exists, workbook, …extras }` (`extraction` on build, `lineId`
 * on add, `updatedLineIds` on group) — callers REPLACE the whole workbook from
 * it, never merge. The workbook is read-only toward the loan: nothing here
 * writes loan terms or the Fees tab.
 */
import { losHttp } from "../lib/losHttp";

const cfg = (logName, extra = {}) => ({ logName, ...extra });
const base = "/closing/settlement_workbook";

export const BUILD_SOURCES = {
  // Every line in four columns + the printed totals; an LLM read, 30–90 s.
  WORKBOOK_EXTRACT: "workbook_extract",
  // Quick seed from the listener's existing HUD extract; no LLM, less complete.
  HUD_EXTRACT: "hud_extract",
};

export const settlementWorkbookApi = {
  // GET /options → { workbookGroups, paidFrom, feesTabBuckets, sections,
  // payeeRoles, paidBy, lineCategories } — the grid's select lists.
  getOptions: () =>
    losHttp
      .get(`${base}/options`, cfg("Settlement workbook: options"))
      .then((r) => r.data),

  // GET /{dealId}/documents → { documents[], uploadDocTypes[] } — the source
  // picker, settlement statements first.
  listDocuments: (dealId) =>
    losHttp
      .get(`${base}/${dealId}/documents`, cfg("Settlement workbook: documents"))
      .then((r) => r.data),

  // GET /{dealId} → { exists: false, workbook: null } until one is built.
  get: (dealId) =>
    losHttp
      .get(`${base}/${dealId}`, cfg("Settlement workbook: get"))
      .then((r) => r.data),

  // POST /{dealId}/build/{documentId} { source, replace } — replace is required
  // when a workbook exists (409 otherwise) and discards the closer's edits. A
  // non-statement comes back { extraction: { ok: false, rejected, error } }.
  build: (dealId, documentId, { source = BUILD_SOURCES.WORKBOOK_EXTRACT, replace = false } = {}) =>
    losHttp
      .post(
        `${base}/${dealId}/build/${encodeURIComponent(documentId)}`,
        { source, replace },
        cfg("Settlement workbook: build", { timeout: 180000 }),
      )
      .then((r) => r.data),

  // POST /{dealId}/lines — a manual line; body = any editable line fields.
  addLine: (dealId, line = {}) =>
    losHttp
      .post(`${base}/${dealId}/lines`, line, cfg("Settlement workbook: add line"))
      .then((r) => r.data),

  // POST /{dealId}/lines/{lineId}/update — partial edit; unknown keys ignored.
  updateLine: (dealId, lineId, patch) =>
    losHttp
      .post(
        `${base}/${dealId}/lines/${lineId}/update`,
        patch,
        cfg("Settlement workbook: update line"),
      )
      .then((r) => r.data),

  // POST /{dealId}/lines/{lineId}/revert — back to the extracted values
  // (grouping kept). 409 for a manual line.
  revertLine: (dealId, lineId) =>
    losHttp
      .post(
        `${base}/${dealId}/lines/${lineId}/revert`,
        {},
        cfg("Settlement workbook: revert line"),
      )
      .then((r) => r.data),

  // POST /{dealId}/group { lineIds, workbookGroup?, paidFrom?, feesTabBucket?,
  // included? } — only the keys sent are applied.
  group: (dealId, body) =>
    losHttp
      .post(`${base}/${dealId}/group`, body, cfg("Settlement workbook: group"))
      .then((r) => r.data),

  // POST /{dealId}/status { status: 'draft' | 'reviewed' }.
  setStatus: (dealId, status) =>
    losHttp
      .post(`${base}/${dealId}/status`, { status }, cfg("Settlement workbook: status"))
      .then((r) => r.data),
};

/** The backend's error sentence, for a toast. */
export const workbookErrorMessage = (err, fallback) =>
  err?.response?.data?.detail ||
  err?.response?.data?.message ||
  fallback;

export default settlementWorkbookApi;
